import Cookie from 'js-cookie';
import fetch from 'cross-fetch';
import markdownIt from 'markdown-it';

function createUser ({ status, data }, baseUrl, schoolId, devMode) {
  return {
    status,
    user: {
      isAuthenticated() {
        return status === 200
      },
      getName() {
        return data
          ? data.name
          : null
      },
      loginRedirect(opts = {}) {
        if (status !== 200) {
          opts.schoolId = schoolId;
          opts.devMode = devMode;
          const searchParams = new URLSearchParams(opts);
          window.location.href = `${baseUrl}/login?${searchParams.toString()}`;
        } else {
          console.error('Already logged in.');
        }
      },
      async requestLoginLink(email, opts = {}) {
        if (status !== 200) {
          await fetch(
            `${baseUrl}/login`,
            {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                destination: {
                  courseId: opts.courseId,
                  email,
                  schoolId,
                  devMode
                }
              })
            }
          );
        } else {
          console.error('Already logged in.');
        }
      },
      logoutRedirect(opts = {}) {
        if (status === 200) {
          Cookie.remove('access_token');
          Cookie.remove('refresh_token');
          opts.schoolId = schoolId;
          opts.devMode = devMode;
          const searchParams = new URLSearchParams(opts);
          window.location.href = `${baseUrl}/logout?${searchParams.toString()}`;
        } else {
          return console.error('Already logged out.')
        }
      },
      enrollRedirect(courseId, name = null, email = null) {
        if (!courseId) {
          return console.error('Course ID must be supplied.')
        }
        const searchParams = new URLSearchParams({ courseId,
          name,
          email,
          schoolId,
          devMode
        });

        if (email) {
          window.location.href =`${baseUrl}/passwordlessEnroll?${searchParams.toString()}`;
        } else {
          window.location.href =`${baseUrl}/enroll?${searchParams.toString()}`;
        }
      },
    }
  }
}

function isCourseEnrolled(courseId, user) {
  if (!user) {
    return null
  } else {
    return !!user.courses.find(course => course.id === courseId)
  }
}

function getNextLesson(currentLessonId, lessons) {
  const currentLessonIndex = lessons.findIndex(lesson => lesson.id === currentLessonId);
  if (currentLessonIndex === -1 || currentLessonIndex === lessons.length - 1) {
    return null
  }
  return lessons[currentLessonIndex + 1]
}

function getPreviousLesson(currentLessonId, lessons) {
  const currentLessonIndex = lessons.findIndex(lesson => lesson.id === currentLessonId);
  if (currentLessonIndex === -1 || currentLessonIndex === 0) {
    return null
  }
  return lessons[currentLessonIndex - 1]
}

function getNextIncompleteLesson(lessons) {
  return lessons.find(lesson => !lesson.complete)
}

function getProgress(lessons) {
  if (lessons.length === 0) {
    return 1
  }
  const complete = lessons.filter(lesson => lesson.complete);
  return Math.round(100 * complete.length / lessons.length) / 100
}

function isLessonComplete(courseId, lessonId, user) {
  const course = user.courses.find(course => course.id === courseId);
  if (course) {
    const lesson = course.lessons.find(lesson => lesson.id === lessonId);
    if (lesson) {
      return lesson.complete
    }
  }
}

function getCompletedAt(courseId, lessonId, user) {
  const course = user.courses.find(course => course.id === courseId);
  if (course) {
    const lesson = course.lessons.find(lesson => lesson.id === lessonId);
    if (lesson) {
      return lesson.completedAt
    }
  }
}

const md$1 = markdownIt();

function createCourse ({ status, data }, user) {
  let html = null;
  let markdown = null;
  let meta;
  if (data.publicContent && data.publicContent.content) {
    html = md$1.render(data.publicContent.markdown);
    markdown = data.publicContent.markdown;
  } else {
    if (user) {
      html = md$1.render(data.privateContent.markdown);
      markdown = data.privateContent.markdown;
    }
  }
  if (user) {
    meta = {
      ...data.publicContent,
      ...data.privateContent,
    };
  } else {
    meta = {
      ...data.publicContent
    };
  }
  delete meta.markdown;
  const lessons = data.lessons.map(lesson => ({
    id: lesson.id,
    title: lesson.title,
    order: lesson.order,
    complete: user ? isLessonComplete(data.id, lesson.id, user) : null,
    completedAt: user ? getCompletedAt(data.id, lesson.id, user) : null,
    meta: {
      ...lesson.publicContent
    }
  })).sort((a, b) => (a.order >= b.order ? 1 : -1));
  const nextIncompleteLesson = user ? getNextIncompleteLesson(lessons) : null;
  return {
    status,
    course: {
      id: data.id,
      title: data.title,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      enrolled: isCourseEnrolled(data.id, user),
      getNextLesson: (currentLessonId) => getNextLesson(currentLessonId, lessons),
      nextIncompleteLesson: nextIncompleteLesson,
      getPreviousLesson: (currentLessonId) => getPreviousLesson(currentLessonId, lessons),
      // deprecating nextLessonId in v1
      nextLessonId: nextIncompleteLesson ? nextIncompleteLesson.id : null,
      progress: user ? getProgress(lessons): null,
      lessons,
      meta,
      markdown,
      html,
    }
  }
}

function createCourses ({ status, data }, user) {
  return {
    status,
    courses: data.map((course) => {
      return {
        id: course.id,
        title: course.title,
        enrolled: user ? isCourseEnrolled(course.id, user) : null,
        meta: {
          ...course.publicContent
        }
      }
    })
  }
}

const md = markdownIt();

function createLesson ({ status, data }, courseId, user, api) {
  let html = null;
  let markdown = null;
  let complete = false;
  let completedAt = null;
  let meta;
  if (data.publicContent && data.publicContent.content) {
    html = md.render(data.publicContent.markdown);
    markdown = data.publicContent.markdown;
  } else {
    if (status === 200) {
      html = md.render(data.privateContent.markdown);
      markdown = data.privateContent.markdown;
    }
  }
  if (status === 200) {
    complete = user ? isLessonComplete(courseId, data.id, user) : false;
    completedAt = user ? getCompletedAt(courseId, data.id, user) : null;
  }
  meta = {
    ...data.publicContent,
    ...data.privateContent,
  };
  delete meta.markdown;
  return {
    status,
    lesson: {
      id: data.id,
      title: data.title,
      order: data.order,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      meta,
      markdown,
      html,
      complete,
      completedAt,
      async markComplete() {
        if (status === 200) {
          if (complete) {
            return true
          }
          return await api.setComplete(courseId, data.id, true, user)
        }
        return false
      },
      async markIncomplete() {
        if (status === 200) {
          if (!complete) {
            return true
          }
          return await api.setComplete(courseId, data.id, false, user)
        }
        return false
      }
    }
  }
}

function createCache (baseUrl, schoolId, devMode, api) {
  return {
    store: {
      user: null,
      courses: [],
      allCourses: null,
      lessons: []
    },
    addCourses(response) {
      this.store.allCourses = response;
      return this.getCourses()
    },
    addCourse(courseId, response) {
      this.store.courses.push({ courseId, response });
      return this.getCourse(courseId)
    },
    addLesson(courseId, lessonId, response) {
      this.store.lessons.push({ courseId, lessonId, response });
      return this.getLesson(courseId, lessonId)
    },
    addUser(response) {
      this.store.user = response;
      return this.getUser()
    },
    getCourses() {
      const courses = this.store.allCourses;
      if (courses) {
        if (this.store.user && this.store.user.status === 200) {
          const user = this.store.user.data;
          return createCourses(courses, user)
        } else {
          return createCourses(courses, null)
        }
      } else {
        return null
      }
    },
    getCourse(courseId) {
      const course = this.store.courses.find(course => {
        return course.courseId === courseId
      });
      if (course) {
        if (course.response.status === 404) {
          return { status: 404, course: null }
        }
        if (this.store.user && this.store.user.status === 200) {
          const user = this.store.user.data;
          return createCourse(course.response, user)
        } else {
          return createCourse(course.response)
        }
      } else {
        return null
      }
    },
    getLesson(courseId, lessonId) {
      const lesson = this.store.lessons.find(lesson => {
        return lesson.courseId === courseId && lesson.lessonId === lessonId
      });
      if (lesson) {
        if (lesson.response.status === 404) {
          return { status: 404, lesson: null }
        }
        if (this.store.user && this.store.user.status === 200) {
          const user = this.store.user.data;
          return createLesson(lesson.response, courseId, user, api)
        } else {
          return createLesson(lesson.response, courseId, null, api)
        }
      } else {
        return null
      }
    },
    getUser() {
      const user = this.store.user;
      if (user) {
        return createUser(user, baseUrl, schoolId, devMode)
      } else {
        return null
      }
    }
  }
}

function tokenSet(name, value, opts, isCookie = true) {
  if (isCookie) {
    return Cookie.set(name, value, opts)
  } else {
    return sessionStorage.setItem(name, value)
  }
}

function tokenGet(name, isCookie = true) {
  if (isCookie) {
    return Cookie.get(name)
  } else {
    return sessionStorage.getItem(name)
  }
}

function tokenRemove(name, isCookie = true) {
  if (isCookie) {
    return Cookie.remove(name)
  } else {
    return sessionStorage.removeItem(name)
  }
}

function Response (status, data) {
  return { status, data }
}

let accessToken;
let refreshToken;

async function refresh(baseUrl, authSessionStorage) {
  const res = await fetch(
    `${baseUrl}/v1/oauth2/token`,
    {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refreshToken
      })
    }
  );
  if (res.status === 200) {
    const data = await res.json();
    accessToken = data.access_token;
    refreshToken = data.refresh_token;
    const accessExpiry = new Date(new Date().getTime() + (3600 * 1000));
    const refreshExpiry = new Date(new Date().getTime() + (43200 * 60 * 1000));
    tokenSet('access_token', accessToken, { expires: accessExpiry}, !authSessionStorage);
    tokenSet('refresh_token', refreshToken, { expires: refreshExpiry }, !authSessionStorage);
  } else {
    accessToken = null;
    refreshToken = null;
    tokenRemove('access_token', !authSessionStorage);
    tokenRemove('refresh_token', !authSessionStorage);
  }
}

async function get(path, jsonOnUnauth = true, retries = 1, authSessionStorage) {
  const res = await fetch(
    path,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }
  );
  if (res.status === 401 && refreshToken) {
    accessToken = tokenGet('access_token', !authSessionStorage);
    if (!accessToken && retries > 0) {
      retries--;
      const url = new URL(path);
      await refresh(url.origin, authSessionStorage);
      return get(path, jsonOnUnauth, retries, authSessionStorage)
    }
  }
  const data = (res.status === 200 || jsonOnUnauth) ? await res.json() : null;
  return Response(res.status, data)
}

/**
 * This function checks if the item (course or lesson) is published.
 * We assume status is a property of the publicContent object.
 * However, not all courses will use this property, so we assume the item is published
 * if no status property can be found.
 * @param obj
 * @returns {boolean}
 */
function isPublished(obj) {
  if (obj?.publicContent?.status) {
    return obj.publicContent.status === "published"
  }
  return true
}

function createApi(baseUrl, tokens, devMode, authSessionStorage = false) {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  return {
    async loadUser() {
      return get(`${baseUrl}/user`, false, 1, authSessionStorage)
    },
    async loadCourses (schoolId) {
      const res = await get(`${baseUrl}/schools/${schoolId}`, true, 1, authSessionStorage);
      return {
        status: res.status,
        data: res.data.filter(course => devMode ? true : isPublished(course))
      }
    },
    async loadCourse (courseId) {
      const res = await get(`${baseUrl}/courses/${courseId}`, true, 1, authSessionStorage);
      if (devMode ? true : isPublished(res.data)) {
        const data = { ...res.data };
        data.lessons = data.lessons.filter(lesson => devMode ? true : isPublished(lesson));
        return {
          status: res.status,
          data
        }
      } else {
        return {
          status: 404,
          data: null
        }
      }
    },
    async loadLesson (courseId, lessonId) {
      const res = await get(`${baseUrl}/courses/${courseId}/lessons/${lessonId}`, true, 1, authSessionStorage);
      if (devMode ? true : isPublished(res.data)) {
        return res
      } else {
        return {
          status: 404,
          data: null
        }
      }
    },
    async setComplete(courseId, lessonId, isComplete, user) {
      try {
        const response = await fetch(
          `${baseUrl}/courses/${courseId}/lessons/${lessonId}`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ complete: isComplete })
          }
        );
        if (response.status === 200) {
          const course = user.courses.find(course => course.id === courseId);
          const lesson = course.lessons.find(lesson => lesson.id === lessonId);
          lesson.complete = isComplete;
          return true
        } else {
          return false
        }
      } catch (err) {
        console.log(err);
        return { status: 500 }
      }
    }
  }
}

const baseUrl = 'https://api.coursekit.dev/v1';

function createErrorObject(err, status = 500) {
  console.log(err);
  return { status }
}

function getTokens (authSessionStorage) {
  let accessToken;
  let refreshToken;
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    accessToken = urlParams.get('access_token');
    refreshToken = urlParams.get('refresh_token');
    if (accessToken && refreshToken) {
      const accessExpiry = new Date(new Date().getTime() + (3600 * 1000));
      const refreshExpiry = new Date(new Date().getTime() + (43200 * 60 * 1000));
      tokenSet('access_token', accessToken, { expires: accessExpiry}, !authSessionStorage);
      tokenSet('refresh_token', refreshToken, { expires: refreshExpiry }, !authSessionStorage);
      urlParams.delete('access_token');
      urlParams.delete('refresh_token');
      const url = window.location.href.split('?')[0] + '?' + urlParams.toString();
      window.history.replaceState({}, document.title, url);
    } else {
      accessToken = tokenGet('access_token', !authSessionStorage);
      refreshToken = tokenGet('refresh_token', !authSessionStorage);
    }
  }
  return { accessToken, refreshToken }
}

class CourseKitClient {
  constructor(opts) {
    if (!opts || !opts.schoolId) {
      throw new Error('You must provide a schoolId option in the constructor.')
    }
    this.schoolId = opts.schoolId;
    const devMode = opts.devMode || false;
    const tokens = getTokens(opts.authSessionStorage);
    this.api = createApi(opts.baseUrl || baseUrl, tokens, devMode, opts.authSessionStorage);
    this.cache = createCache(opts.baseUrl || baseUrl, opts.schoolId, devMode, this.api);
  }

  async loadUser() {
    let obj = this.cache.getUser();
    if (!obj) {
      try {
        const response = await this.api.loadUser();
        obj = this.cache.addUser(response);
      } catch (err) {
        obj = createErrorObject(err);
      }
    }
    return obj
  }

  async loadCourseSummaries() {
    let obj = this.cache.getCourses();
    if (!obj) {
      try {
        const response = await this.api.loadCourses(this.schoolId);
        obj = this.cache.addCourses(response);
      } catch (err) {
        obj = createErrorObject(err);
      }
    }
    return obj
  }

  async loadCourse(courseId) {
    let obj = this.cache.getCourse(courseId);
    if (!obj) {
      try {
        const response = await this.api.loadCourse(courseId);
        obj = this.cache.addCourse(courseId, response);
      } catch (err) {
        obj = createErrorObject(err);
      }
    }
    return obj
  }

  async loadLesson(courseId, lessonId) {
    let obj = this.cache.getLesson(courseId, lessonId);
    if (!obj) {
      try {
        const response = await this.api.loadLesson(courseId, lessonId);
        obj = this.cache.addLesson(courseId, lessonId, response);
      } catch (err) {
        obj = createErrorObject(err);
      }
    }
    return obj
  }
}

export { CourseKitClient };
